export const DEFAULT_MIN_BLOCK_WIDTH_PX = 12;

function parseTimeMs(value) {
  const parsed = value instanceof Date ? value.getTime() : Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function clipWindow(window, rangeStartMs, rangeEndMs) {
  const startMs = Number.isFinite(Number(window?.startMs))
    ? Number(window.startMs)
    : parseTimeMs(window?.start);
  const endMs = Number.isFinite(Number(window?.endMs))
    ? Number(window.endMs)
    : parseTimeMs(window?.end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return null;
  }

  const clippedStartMs = Math.max(startMs, rangeStartMs);
  const clippedEndMs = Math.min(endMs, rangeEndMs);
  if (clippedEndMs <= clippedStartMs) return null;

  return {
    startMs: clippedStartMs,
    endMs: clippedEndMs,
  };
}

export function normalizeVisibleWindows(visibleWindows, rangeStart, rangeEnd) {
  const rangeStartMs = parseTimeMs(rangeStart);
  const rangeEndMs = parseTimeMs(rangeEnd);
  if (!Number.isFinite(rangeStartMs) || !Number.isFinite(rangeEndMs) || rangeEndMs <= rangeStartMs) {
    return [];
  }

  const normalized = (Array.isArray(visibleWindows) ? visibleWindows : [])
    .map((window) => clipWindow(window, rangeStartMs, rangeEndMs))
    .filter(Boolean)
    .sort((left, right) => left.startMs - right.startMs);

  if (!normalized.length) {
    return [
      {
        startMs: rangeStartMs,
        endMs: rangeEndMs,
      },
    ];
  }

  return normalized.reduce((merged, window) => {
    const last = merged[merged.length - 1];
    if (!last || window.startMs > last.endMs) {
      merged.push(window);
      return merged;
    }
    last.endMs = Math.max(last.endMs, window.endMs);
    return merged;
  }, []);
}

export function getVisibleDurationMs({ rangeStart, rangeEnd, visibleWindows }) {
  return normalizeVisibleWindows(visibleWindows, rangeStart, rangeEnd).reduce(
    (total, window) => total + (window.endMs - window.startMs),
    0
  );
}

export function projectTimeToLaneX({
  time,
  rangeStart,
  rangeEnd,
  laneWidth,
  visibleWindows,
}) {
  const timeMs = parseTimeMs(time);
  const safeLaneWidth = Number(laneWidth);
  if (!Number.isFinite(timeMs) || !Number.isFinite(safeLaneWidth) || safeLaneWidth <= 0) {
    return null;
  }

  const windows = normalizeVisibleWindows(visibleWindows, rangeStart, rangeEnd);
  const totalVisibleMs = windows.reduce((total, window) => total + (window.endMs - window.startMs), 0);
  if (!totalVisibleMs) return null;

  let visibleOffsetMs = 0;
  for (const window of windows) {
    if (timeMs < window.startMs) {
      return null;
    }
    if (timeMs >= window.startMs && timeMs <= window.endMs) {
      return ((visibleOffsetMs + (timeMs - window.startMs)) / totalVisibleMs) * safeLaneWidth;
    }
    visibleOffsetMs += window.endMs - window.startMs;
  }

  return null;
}

export function getTimelineBlockLayout({
  startsAt,
  endsAt,
  rangeStart,
  rangeEnd,
  laneWidth,
  minWidth = DEFAULT_MIN_BLOCK_WIDTH_PX,
  visibleWindows = null,
}) {
  const startMs = parseTimeMs(startsAt);
  const endMs = parseTimeMs(endsAt);
  const rangeStartMs = parseTimeMs(rangeStart);
  const rangeEndMs = parseTimeMs(rangeEnd);
  const safeLaneWidth = Number(laneWidth);
  const windows = normalizeVisibleWindows(visibleWindows, rangeStart, rangeEnd);
  const durationMs = windows.reduce((total, window) => total + (window.endMs - window.startMs), 0);

  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    !Number.isFinite(rangeStartMs) ||
    !Number.isFinite(rangeEndMs) ||
    !Number.isFinite(safeLaneWidth) ||
    safeLaneWidth <= 0 ||
    durationMs <= 0
  ) {
    return null;
  }

  let visibleOffsetStartMs = null;
  let visibleWidthMs = 0;
  let accumulatedMs = 0;

  windows.forEach((window) => {
    const overlapStartMs = Math.max(startMs, window.startMs);
    const overlapEndMs = Math.min(endMs, window.endMs);
    if (overlapEndMs > overlapStartMs) {
      if (visibleOffsetStartMs == null) {
        visibleOffsetStartMs = accumulatedMs + (overlapStartMs - window.startMs);
      }
      visibleWidthMs += overlapEndMs - overlapStartMs;
    }
    accumulatedMs += window.endMs - window.startMs;
  });

  if (visibleOffsetStartMs == null || visibleWidthMs <= 0) {
    return null;
  }

  const left = (visibleOffsetStartMs / durationMs) * safeLaneWidth;
  const rawWidth = (visibleWidthMs / durationMs) * safeLaneWidth;
  const width = Math.max(minWidth, rawWidth);

  return {
    left: Math.max(0, left),
    width,
    clippedStart: startMs < rangeStartMs,
    clippedEnd: endMs > rangeEndMs,
  };
}
